import { TTLQueue } from "gather-video-common/dist/src/public/ttlQueue";
import { identity, noop } from "lodash";
import { always, ifElse, mergeRight, partial, pipe, tryCatch } from "ramda";
import { v4 as uuid } from "uuid";
import { encodeAction, encodeDevicesChange, encodeLog, encodeMajorEvent, encodeMetrics, encodeNetworkTestResults, encodePlayerInfo, encodeSFUMessage, encodeSelectDevice, encodeSelfInfo, encodeWebRTCIssue, encodeStateMachineTransition, } from "./encoders";
import { keyGen, lastMessageMiddleware } from "./middleware";
import { getCircularSimplifyDataStructureReplacer } from "./simplifyDataStructureReplacer";
import { wrapTryCatchEncoder } from "./wrapTryCatchEncoder";
const TEN_MINUTES_IN_MS = 1000 * 60 * 10;
const DEFAULT_PRUNE_SIZE_BYTES = 1024 * 1024 * 4;
const MAX_MESSAGE_COUNT = 2048;
export class Tracker {
    maxMessages;
    maxTTL;
    middlewares = [identity];
    applyMiddleware(message) {
        return tryCatch((_message) => this.middlewares.reduce((acc, fn) => fn(acc), _message), always(message))(message);
    }
    addMiddleware(fn) {
        this.middlewares.push(fn);
    }
    removeMiddleware(fn) {
        this.middlewares = this.middlewares.filter((f) => f !== fn);
    }
    metadata = {};
    playerId;
    spaceId;
    sessionId;
    running = false;
    messages;
    lastMessageCache;
    static instance = new Tracker();
    constructor(maxMessages = MAX_MESSAGE_COUNT, maxTTL = TEN_MINUTES_IN_MS) {
        this.maxMessages = maxMessages;
        this.maxTTL = maxTTL;
        this.messages = new TTLQueue(this.maxMessages, maxTTL);
        this.lastMessageCache = new Map();
    }
    start(playerId, spaceId) {
        this.playerId = playerId;
        this.spaceId = spaceId;
        this.running = true;
        this.sessionId = uuid();
    }
    add = (message) => {
        if (this.running) {
            const result = this.applyMiddleware(message);
            this.messages?.push(result);
        }
    };
    getMessages() {
        return this.messages.toArray();
    }
    stop() {
        this.running = false;
        this.sessionId = undefined;
    }
    reset() {
        this.messages = new TTLQueue(this.maxMessages, this.maxTTL);
        this.lastMessageCache = new Map();
    }
    ensureLastMessageFromCache = (messages) => {
        const messagesWithLastMessage = [...messages];
        const uniqueMessageKeys = new Set(messages.map(keyGen));
        this.lastMessageCache.forEach((message, key) => {
            if (uniqueMessageKeys.has(key))
                return;
            messagesWithLastMessage.unshift(message);
        });
        return messagesWithLastMessage;
    };
    dump(limitBytes) {
        this.prune(limitBytes);
        const messages = this.ensureLastMessageFromCache(this.getMessages());
        return JSON.parse(JSON.stringify({
            playerId: this.playerId,
            spaceId: this.spaceId,
            sessionId: this.sessionId,
            ...this.metadata,
            session: messages,
        }, getCircularSimplifyDataStructureReplacer()));
    }
    prune(limitBytes = DEFAULT_PRUNE_SIZE_BYTES) {
        const messages = this.getMessages();
        let currentIndex = messages.length - 1;
        let currentSize = 0;
        if (currentIndex === -1)
            return;
        while (currentSize < limitBytes && currentIndex > -1) {
            currentSize += JSON.stringify(messages[currentIndex], getCircularSimplifyDataStructureReplacer()).length;
            currentIndex--;
        }
        if (currentSize >= limitBytes) {
            const toRemove = currentIndex + 2;
            this.messages.remove(0, toRemove);
        }
    }
    setMetadataAll(values) {
        this.metadata = mergeRight(this.metadata, values);
    }
    setMetadata(key, value) {
        this.metadata[key] = value;
    }
    shouldCapture = (..._) => this.running;
    createCapturer = (encoder) => ifElse(this.shouldCapture, pipe(partial(wrapTryCatchEncoder, [encoder]), this.add), noop);
    captureSelfInfo = this.createCapturer(encodeSelfInfo);
    captureSFUMessage = this.createCapturer(encodeSFUMessage);
    captureLog = this.createCapturer(encodeLog);
    captureMajorEvent = this.createCapturer(encodeMajorEvent);
    capturePlayerInfo = this.createCapturer(encodePlayerInfo);
    captureDeviceChanges = this.createCapturer(encodeDevicesChange);
    captureSelectDevice = this.createCapturer(encodeSelectDevice);
    captureAction = this.createCapturer(encodeAction);
    captureWebRTCIssue = this.createCapturer(encodeWebRTCIssue);
    captureStateMachineTransition = this.createCapturer(encodeStateMachineTransition);
    captureMetrics = this.createCapturer(encodeMetrics);
    captureNetworkTestResults = this.createCapturer(encodeNetworkTestResults);
}
export const tracker = Tracker.instance;
tracker.addMiddleware(lastMessageMiddleware(tracker));
Object.assign(globalThis, { tracker });
//# sourceMappingURL=Tracker.js.map